import type { Author } from "../types";

export type SitePageData = {
	title: string;
	kicker: string;
	lead: string;
	layout?: "editorial" | "shop" | "contact" | "presentation" | "product" | "contest" | "team";
	sections: {
		title: string;
		paragraphs: string[];
		image?: string;
		imageAlt?: string;
		hideTitle?: boolean;
		href?: string;
		ctaLabel?: string;
		author?: Author;
	}[];
	image?: string;
	/** Slug do produto no WooCommerce, para páginas de produto (`layout: "product"`). */
	productSlug?: string;
};

export const sitePages: Record<string, SitePageData> = {
	"/editorial-cosa-nova/": {
		title: "L’editorial",
		kicker: "Cosa Nova 17488",
		lead: "Traduïm mons per generar diàlegs interculturals.",
		layout: "editorial",
		sections: [
			{
				title: "La nostra raó de ser",
				paragraphs: [
					"Cosa Nova 17488 és una editorial independent nascuda a Cadaqués amb la missió de publicar literatura amb criteri, rigor i sensibilitat cultural, posant una atenció especial en la cura i la tutela de la llengua i la cultura catalanes.",
					"Situem el projecte editorial en la relació entre el llibre i les persones que el fan possible, defensant una manera de fer edició que valori el treball creatiu i editorial, respecti els temps de la literatura i promogui condicions dignes i sostenibles per a totes les parts implicades.",
				],
				image:
					"/assets/cadaques-vista-nocturna-esglesia-santa-maria-mar.webp.webp",
				imageAlt:
					"Vista nocturna de Cadaqués amb l’església de Santa Maria il·luminada davant del mar",
			},
			{
				title: "La nostra visió",
				paragraphs: [
					"Cosa Nova 17488 és un projecte arrelat al territori, però no tancat en si mateix, on el llibre és la brúixola i Cadaqués el port d’inspiració. Entenem el territori i la seva comunitat no com un decorat, sinó com un espai viu, amb tradicions, sabers i formes de transmissió pròpies que mereixen respecte, escolta i cura. Des d’aquí establim ponts literaris amb altres llengües i cultures, especialment amb l’italià i el portuguès, construint un catàleg que circula en ambdues direccions: del territori cap al món i del món cap al territori.",
					"Apostem per una **relació activa amb la comunitat local**, a qui considerem part essencial del projecte. Volem que les persones del territori siguin agents actius de l’editorial, ja sigui publicant, traduint, col·laborant o contribuint d’altres maneres a la vida cultural del projecte.",
					"L’editorial es construeix des de l’escolta, la col·laboració i el respecte.",
				],
				image: "/assets/La-nostra-visió.jpg",
				imageAlt: "La nostra visió editorial",
			},
			{
				title: "Els nostres objectius editorials",
				paragraphs: [
					"**Publicar un catàleg coherent de literatura** que combini veus contemporànies amb textos d’altres períodes, sempre des d’un criteri literari exigent i una mirada actual.",
					"**Vetllar per la cura, la transmissió i la dignificació de la llengua i la cultura catalanes**, tant en l’edició original com en la traducció.",
					"**Entendre la traducció com una pràctica estructural d’obertura cultural i literària**, i no com un element secundari del projecte.",
					"**Fomentar el diàleg** entre llengües i tradicions literàries, especialment entre el català, el portuguès i l’italià.",
					"**Promoure la participació activa de la comunitat local** en el projecte editorial, reconeixent i respectant les seves tradicions, sabers i formes d’expressió.",
					"**Desenvolupar una manera de fer editorial sostenible, tant a nivell econòmic com humà**, que dignifiqui el treball creatiu i editorial.",
					"**Crear comunitat al voltant del llibre** mitjançant activitats culturals, mediació lectora i projectes col·laboratius que ampliïn l’espai de la literatura més enllà de la publicació.",
					"**Contribuir a un ecosistema cultural viu**, arrelat i col·laboratiu, on l’edició sigui també una forma de cura, responsabilitat i continuïtat cultural.",
				],
				image: "/assets/cadaques.jpg",
				imageAlt:
					"Vista de Cadaqués, entorn mediterrani que inspira el projecte editorial",
			},
		],
	},
	"/qui-som/": {
		title: "Nosaltres",
		kicker: "L’equip",
		lead: "Un projecte petit, independent i arrelat al Cap de Creus.",
		layout: "team",
		sections: [
			{
				title: "Veronica Albuquerque",
				paragraphs: [
					"La Veronica viu a Cap de Creus, entre el mar i la muntanya i té per peus arrels aquàtiques. Escriu **per desembolicar les llengües que habiten el seu cap i per donar veu al que silencia amb la boca.**",
					"Per a ella, és una manera d’aprendre a viure mentre es viu. Li encanta escoltar les històries dels infants i dels més grans, i troba en la lectura la paciència que a vegades li manca amb els adults. Mare de tres filles, té tres llibres publicats i uns quants més esperant el vent just per sortir a navegar.",
				],
				image: "/assets/Veronica-Botelho-Diretora.webp",
				imageAlt: "Veronica Albuquerque, directora de Cosa Nova 17488",
				hideTitle: true,
			},
			{
				title: "Ester Enrich Coma",
				paragraphs: [
					"L’Ester Enrich Coma comparteix jardí amb el gat del veí, treballa mentre dorm i somia desperta, **ha escrit força llibres, si bé n’hi han publicat tres i ha acompanyat a escriure’n més dels que és capaç de llegir.**",
					"És filla de bruixes i de contistes i, amb això, fa el que pot. Un dia trobarà l’encanteri que la tornarà best-seller, mentrestant, es diverteix recordant al veïnat que ella ve del planeta Glx i que està de passada i que s’ho passa bé.",
				],
				image: "/assets/Ester-Enrich-Coma-editora.webp",
				imageAlt: "Ester Enrich Coma, editora de Cosa Nova 17488",
				hideTitle: true,
			},
		],
	},
	"/botiga/": {
		title: "Llibres",
		kicker: "Botiga",
		lead: "Històries que neixen entre el mar, el territori i les llengües.",
		layout: "shop",
		sections: [
			{
				title: "50 Tacus",
				paragraphs: [
					"Construït a partir de fragments, llegit com un tot: cinquanta talls d’una mateixa fusta.",
				],
				image: "/assets/50-tacus.jpg",
				imageAlt: "Llibre 50 Tacus",
				href: "/producte/50-tacus/",
				ctaLabel: "Veure el llibre",
			},
			{
				title: "Cartografia de l’estrany · Mapa I: Finestres",
				paragraphs: [
					"Setze veus catalanes travessen l’insòlit en un atles portàtil.",
				],
				image: "/assets/finesterres.jpeg",
				imageAlt: "Llibre Cartografia de l’estrany, Mapa I: Finestres",
				href: "/producte/cartografia-de-lestrany-mapa-i-finestres/",
				ctaLabel: "Veure el llibre",
			},
			{
				title: "Pur",
				paragraphs: [
					"Nara Vidal. Premi APCA 2024 a la millor novel·la brasilera.",
				],
				image: "/assets/MOCKUPS_LIVROS/PUR_COSANOVA.png",
				imageAlt: "Llibre Pur, de Nara Vidal",
				href: "/producte/pur-nara-vidal/",
				ctaLabel: "Veure el llibre",
			},
		],
	},
	"/producte/50-tacus/": {
		title: "50 Tacus",
		kicker: "Jordi Pastells Calls",
		lead: "101 microrelats entre l’humor, la ironia i la melangia.",
		layout: "product",
		productSlug: "50-tacus",
		sections: [
			{
				title: "50 Tacus",
				image: "/assets/50-tacus.jpg",
				imageAlt: "Coberta del llibre 50 Tacus, de Jordi Pastells Calls",
				paragraphs: [
					"50 Tacus (Jordi Pastells Calls) és un recull de 101 relats breus escrits al llarg de més d’una dècada, que exploren amb economia de llenguatge i intensitat narrativa els gestos mínims, les contradiccions i les fragilitats de la vida contemporània. El llibre construeix un mosaic d’escenes quotidianes, situacions absurdes, records íntims i ficcions breus que oscil·len entre l’humor, la melangia, la ironia i el desconcert.",
					"L’obra combina microrelats fulminants, peces de caire poètic, narracions fantàstiques i escenes realistes, amb una veu constant i recognoscible. Elements aparentment menors —objectes, paraules, silencis, detalls insignificants— esdevenen detonants narratius que obren reflexions sobre el temps, la memòria, la pèrdua, la identitat i la condició humana.",
					"El conjunt pren la seva forma definitiva després d’un moment de ruptura i fragilitat física, present de manera transversal en alguns dels relats, en què l’escriptura esdevé un espai d’elaboració, resistència i continuïtat.",
					"Cada microrelat va acompanyat d’una iconografia original creada pel mateix autor, concebuda com una extensió visual del text. Lluny d’una funció merament il·lustrativa, aquest treball gràfic dialoga amb la paraula escrita i forma part del mateix dispositiu narratiu, ampliant els registres de lectura i reforçant la coherència formal del conjunt.",
				],
				// TODO(content): fotografia, mini biografia e link do autor pendentes de aprovação.
				author: { name: "Jordi Pastells Calls" },
			},
		],
	},
	"/producte/cartografia-de-lestrany-mapa-i-finestres/": {
		title: "Cartografia de l’estrany — Mapa I: Finestres",
		kicker: "Antologia de contes",
		lead: "Setze veus de la literatura catalana contemporània obren finestres cap a l’insòlit.",
		layout: "product",
		productSlug: "cartografia-de-lestrany-mapa-i-finestres",
		sections: [
			{
				title: "Descripció",
				image: "/assets/finesterres.jpeg",
				imageAlt: "Coberta de Cartografia de l’estrany, Mapa I: Finestres",
				paragraphs: [
					"Cartografia de l’estrany – Mapa I: Finestres és el primer volum d’una col·lecció que explora els límits de la percepció i de la realitat a través del conte.",
					"En aquest llibre, setze veus procedents de diferents punts del domini lingüístic català interpreten la idea de “finestra” com a espai de pas: entre mons, entre memòria i imaginació, entre el quotidià i l’insòlit.",
					"Els relats es mouen entre el realisme màgic, el surrealisme i la sàtira social, configurant un mapa literari fragmentat que reflecteix la riquesa i la diversitat de la narrativa catalana actual.",
					"Amb aquesta primera antologia, la col·lecció Cartografia de l’estrany reivindica el conte com a forma literària i convida el lector a recórrer territoris on la realitat es desplaça i es transforma.",
					"**Autoria:** Anaïs Franquesa Griso, Javi Fernández Mata, Àngels Castelló, Sílvia Alberich Castellanos, Òscar Palazón, Caroline Mowatt, Jaume C. Pons Alorda, Joaquim Biendicho, Veronica B. Albuquerque, Irene Almolda Ardid, Roger Coch, Ester Enrich Coma, Natàlia Cerezo, Jordi Diu, Eulàlia Canal i Jordi Llavina.",
				],
			},
		],
	},
	"/producte/pur-nara-vidal/": {
		title: "Pur",
		kicker: "Nara Vidal",
		lead: "Una novel·la incòmoda sobre la violència disfressada de ciència.",
		layout: "product",
		productSlug: "pur-nara-vidal",
		sections: [
			{
				title: "Pur",
				image: "/assets/pur-presentacio.jpg",
				imageAlt: "Coberta del llibre Pur, de Nara Vidal",
				paragraphs: [
					"«La ciutat es diu Santa Graça —referència de virtut i netedat en el territori nacional.»",
					"El Brasil dels anys 30 es converteix en el laboratori d’una eugenèsia cruel disfressada de ciència. Amb un to directe i desafiador, Nara Vidal teixeix una estructura a diverses veus marcada pel xoc sense concessions entre què diuen, què pensen i què fan els personatges.",
					"Pur ha estat guardonada amb el reconegut Premi APCA 2024 a la millor novel·la brasilera, i traduïda a l’anglès i al francès. Una obra profundament impactant on fons i forma s’alien en una narrativa incòmoda que esdevé reflex de geografies i temps més propers i presents del que voldríem creure.",
					"**Nara Vidal** va néixer a Minas Gerais, Brasil. És escriptora, crítica literària, professora d’escriptura creativa i traductora. Té llibres publicats al Brasil, Portugal, Espanya, Anglaterra, França, Itàlia, els Països Baixos i Mèxic. Amb Pur s’estrena en català.",
				],
			},
		],
	},
	"/concurs/": {
		title: `I Concurs de relats "Blau de vidre"`,
		kicker: "Concurs",
		lead: "Una convocatòria per reivindicar el conte i fomentar la creació en llengua catalana.",
		layout: "contest",
		sections: [],
	},
	"/agenda/": {
		title: "Agenda",
		kicker: "Cosa Nova 17488",
		lead: "Trobades, presentacions i activitats literàries de Cosa Nova.",
		sections: [
			{
				title: "Pròximament",
				paragraphs: [
					"La nova programació es publicarà quan les dates i els espais estiguin confirmats.",
				],
			},
		],
	},
	"/presentacio-antologia-cartografia-de-lestrany-mapa-i-finestres/": {
		title: "Presentació de l’antologia Cartografia de l’estrany — Mapa I: Finestres",
		kicker: "Agenda",
		lead: "Dues trobades a la Sala Meifrèn de Cadaqués per presentar l’antologia.",
		layout: "presentation",
		sections: [
			{
				title: "21 de març a les 17:30 h",
				paragraphs: [
					"Sala Meifrèn, Cadaqués (Plaça Doctor Trèmols, 1).",
					"La presentació anirà a càrrec de Veronica B. Albuquerque, directora de Cosa Nova 17488, i d’Ester Enrich Coma, curadora de l’antologia.",
					"Comptarem amb la participació d’alguns dels seus autors i autores.",
					"Amb música en viu d’ÀLVAR RODA, en el marc del Dia Mundial de la Poesia.",
				],
			},
			{
				title: "28 de març a les 10:00 h",
				paragraphs: [
					"Sala Meifrèn, Cadaqués (Plaça Doctor Trèmols, 1).",
					"La presentació anirà a càrrec d’Ester Enrich Coma, curadora de l’antologia.",
					"Comptarem amb la participació dels autors i autores de l’obra i de la directora de l’editorial, Veronica B. Albuquerque.",
					"Hi haurà refrigeri i brindis!",
				],
			},
		],
	},
	"/contacte/": {
		title: "Contacte",
		kicker: "Parlem?",
		lead: "Ens trobaràs a Cadaqués, entre el mar i les muntanyes del Cap de Creus.",
		layout: "contact",
		sections: [
			{
				title: "Cosa Nova 17488",
				paragraphs: ["Cadaqués, 17488 · Girona", "info@cosanova.cat"],
			},
		],
	},
	"/guia-wordpress/": {
		title: "Guia de migració a WordPress",
		kicker: "Documentació del projecte",
		lead: "Passo a passo para transformar o design da Cosa Nova 17488 em um tema WordPress personalizado, preservando conteúdo, loja e identidade visual.",
		sections: [
			{
				title: "1. Preparar um ambiente seguro",
				paragraphs: [
					"Nunca desenvolva diretamente no site publicado. Faça um backup completo do banco de dados, de wp-content/uploads, do tema atual, dos plugins e das configurações do WooCommerce.",
					"Crie um ambiente local ou de staging com as mesmas versões de WordPress, WooCommerce, PHP e plugins do site de produção.",
					"No ambiente de desenvolvimento, habilite WP_DEBUG e WP_DEBUG_LOG, mantendo WP_DEBUG_DISPLAY desativado. Desative essas opções antes da publicação.",
				],
			},
			{
				title: "2. Separar tema e funcionalidade",
				paragraphs: [
					"O tema deve controlar cores, fontes, espaçamento, templates, header, footer, cards e apresentação visual.",
					"Funcionalidades que precisam sobreviver à troca de tema devem ficar em plugins: Agenda, eventos, colaboradores, formulário e outros tipos de conteúdo personalizados.",
					"Produtos, estoque, carrinho, checkout, conta e pedidos permanecem sob responsabilidade do WooCommerce.",
				],
			},
			{
				title: "3. Criar a estrutura do tema",
				paragraphs: [
					"Crie o diretório wp-content/themes/cosanova com style.css, theme.json, functions.php e screenshot.png.",
					"Adicione as pastas assets/css, assets/js, assets/fonts, assets/images, parts, templates, patterns, blocks e inc.",
					"Em templates, prepare index.html, front-page.html, page.html, single.html, archive.html, single-product.html, archive-product.html e 404.html. Em parts, crie header.html e footer.html.",
				],
			},
			{
				title: "4. Registrar e configurar o tema",
				paragraphs: [
					"Registre em style.css o nome Cosa Nova 17488, versão, autor, text domain e requisitos mínimos de WordPress e PHP.",
					"Use theme.json como fonte principal dos tokens: azul #253a64, branco #ffffff, creme #fffaf6, amarelo #ffff00, vermelho #d8232a, fonte Archivo e largura ampla de 1310px.",
					"No functions.php, habilite estilos de blocos, estilos do editor, embeds responsivos, WooCommerce e os recursos de zoom, lightbox e slider da galeria de produtos.",
					"Carregue os arquivos do tema com wp_enqueue_style e wp_enqueue_script, usando a versão do tema para invalidar cache.",
				],
			},
			{
				title: "5. Converter header, footer e home",
				paragraphs: [
					"Converta Header, MainNav, SideMenu e Footer em parts/header.html e parts/footer.html. Use os blocos nativos Site Logo, Navigation, Social Links, Group e Columns.",
					"O menu deve ser editável pelo painel do WordPress. Mantenha somente o comportamento móvel em JavaScript próprio.",
					"Organize front-page.html com: manifesto da marca, carousel, concurso, livros, editorial, apresentação, agenda, mosaico editorial e footer.",
					"Use patterns para estruturas estáticas e editáveis, como Brand Statement, apresentação editorial, contato e cabeçalhos legais.",
				],
			},
			{
				title: "6. Converter os componentes",
				paragraphs: [
					"BrandStatement, FeatureSection e EditorialTiles podem virar patterns ou composições de blocos nativos.",
					"BannerCarousel, ConcursScroll e CollaboratorsCarousel devem virar blocos personalizados, pois possuem comportamento e animação específicos.",
					"ContentPage deve ser distribuído entre page.html e templates específicos. BlockGrid deve ser substituído por Query Loop ou blocos de produtos do WooCommerce.",
					"Cada bloco personalizado deve possuir block.json, index.js, edit.js, save.js, style.css e view.js. A edição fica em edit.js e o comportamento do frontend em view.js.",
				],
			},
			{
				title: "7. Migrar as animações",
				paragraphs: [
					"Preserve o HTML semântico e converta os hooks React para JavaScript modular. Use IntersectionObserver, requestAnimationFrame, propriedades CSS e position: sticky.",
					"O conteúdo deve continuar legível quando JavaScript falhar. Nunca dependa da animação para revelar informações essenciais.",
					"Crie uma alternativa com prefers-reduced-motion que remova movimentos contínuos e transforme os componentes em layouts estáticos.",
				],
			},
			{
				title: "8. Modelar o conteúdo",
				paragraphs: [
					"Use páginas WordPress para Editorial, Nosaltres, Contacte, Avís legal, Política de privacitat, devoluções e Concurs.",
					"Use posts ou um Custom Post Type para agenda, notícias, eventos e apresentações. O tipo Evento pode ter título, data, hora, local, endereço, descrição, imagem e colaboradores.",
					"Mantenha o Custom Post Type em um plugin próprio para que os dados não desapareçam ao trocar o tema.",
				],
			},
			{
				title: "9. Integrar o WooCommerce",
				paragraphs: [
					"Mantenha os livros como produtos, usando imagem destacada, galeria, descrição curta, descrição completa, preço, estoque, peso, envio, categorias e atributos.",
					"Crie archive-product.html e single-product.html usando blocos e hooks oficiais. Evite copiar templates PHP do plugin sem necessidade, pois overrides exigem manutenção após atualizações.",
					"Mantenha os blocos oficiais de carrinho e checkout e teste produto, estoque, cupom, frete, pagamento, conta, pedido e e-mails transacionais.",
				],
			},
			{
				title: "10. Implementar o formulário",
				paragraphs: [
					"Substitua o mailto atual por um plugin consolidado de formulários ou por um bloco dinâmico que use wp_mail.",
					"Implemente nonce, sanitização, validação de e-mail, proteção antispam, mensagens de sucesso e erro, consentimento de privacidade e SMTP autenticado.",
				],
			},
			{
				title: "11. Migrar estilos e arquivos",
				paragraphs: [
					"Organize o CSS em tokens.css, base.css, typography.css, layout.css, components.css, woocommerce.css e site.css.",
					"Migre nesta ordem: tokens, tipografia, container e grid, header e footer, botões, componentes, responsividade e WooCommerce.",
					"Copie imagens e fontes para assets e use get_theme_file_uri para gerar URLs. Não mantenha URLs absolutas do ambiente de desenvolvimento.",
				],
			},
			{
				title: "12. Testar o tema",
				paragraphs: [
					"Valide home, menus, páginas institucionais, carousel, concurso, produtos, carrinho, checkout, conta, formulário, links e página 404.",
					"Teste larguras de 320px, 768px, 1024px e 1440px, navegação por teclado, leitores de tela, contraste e preferência por movimento reduzido.",
					"Verifique o editor de blocos: o conteúdo no painel deve representar com fidelidade razoável o resultado do frontend.",
				],
			},
			{
				title: "13. Publicar com segurança",
				paragraphs: [
					"Faça um novo backup e instale o tema primeiro em staging. Compare todas as URLs antigas e novas e crie redirecionamentos 301 quando os slugs mudarem.",
					"Preserve títulos SEO, descrições, canonicals, dados estruturados, URLs de produtos e imagens importantes.",
					"Antes da ativação, teste uma compra completa, e-mails, formulário e WooCommerce > Status > Templates. Publique em horário de baixo tráfego e monitore logs, pagamentos e pedidos.",
				],
			},
			{
				title: "Ordem recomendada",
				paragraphs: [
					"1. Tema vazio e theme.json. 2. Header e footer. 3. Templates de páginas. 4. Home estática. 5. Produtos e WooCommerce. 6. Carousel. 7. Concurso animado.",
					"8. Agenda e apresentação. 9. Colaboradores. 10. Formulário. 11. Conteúdo e mídia. 12. Responsividade e acessibilidade. 13. SEO e redirecionamentos. 14. Staging e publicação.",
				],
			},
		],
	},
	"/avis-legal/": {
		title: "Avís legal",
		kicker: "Informació legal",
		lead: "Informació sobre la titularitat i les condicions d’ús del lloc web.",
		sections: [
			{
				title: "Titularitat",
				paragraphs: [
					"En compliment del que disposa la Llei 34/2002, d’11 de juliol, de serveis de la societat de la informació i de comerç electrònic (LSSI-CE), s’informa que aquest lloc web és titularitat de:",
					"Titular: COSA NOVA 17488, S.L.",
					"NIF: B24744716",
					"Domicili social: Cadaqués (Girona), Espanya",
					"Correu electrònic: info@cosanova.cat",
					"Nom del domini: www.cosanova.cat",
				],
			},
			{
				title: "Objecte",
				paragraphs: [
					"Aquest lloc web té com a objecte proporcionar informació sobre les activitats editorials, culturals i comercials de COSA NOVA 17488, així com permetre la compra de productes i la participació en activitats, si escau.",
				],
			},
			{
				title: "Condicions d’ús",
				paragraphs: [
					"L’accés i ús d’aquest lloc web atribueix la condició d’usuari/a, que accepta, des d’aquest accés, les presents condicions d’ús.",
					"L’usuari/a es compromet a fer un ús adequat dels continguts i serveis que COSA NOVA 17488 ofereix a través del lloc web, i a no emprar-los per a activitats il·lícites, contràries a la bona fe o a l’ordre públic.",
				],
			},
			{
				title: "Propietat intel·lectual i industrial",
				paragraphs: [
					"Tots els continguts del lloc web (textos, imatges, dissenys, logotips, tipografies, arxius, etc.) són titularitat de COSA NOVA 17488 o de tercers que n’han autoritzat l’ús, i estan protegits per la normativa vigent en matèria de propietat intel·lectual i industrial.",
					"Queda prohibida la reproducció, distribució o comunicació pública, total o parcial, dels continguts sense autorització expressa del titular.",
				],
			},
			{
				title: "Responsabilitat",
				paragraphs: [
					"COSA NOVA 17488 no es fa responsable dels danys o perjudicis que es puguin derivar de l’ús del lloc web ni de possibles errors tècnics o interrupcions del servei.",
					"Aquest lloc web pot contenir enllaços a pàgines de tercers. COSA NOVA 17488 no es responsabilitza del contingut ni de les polítiques de privacitat d’aquests llocs externs.",
				],
			},
			{
				title: "Legislació aplicable",
				paragraphs: [
					"La relació entre COSA NOVA 17488 i l’usuari/a es regirà per la normativa vigent a l’Estat espanyol. Qualsevol controvèrsia se sotmetrà als jutjats i tribunals que corresponguin conforme a dret.",
				],
			},
		],
	},
	"/politica-privacitat/": {
		title: "Política de privacitat",
		kicker: "Privacitat",
		lead: "COSA NOVA 17488 es compromet a protegir la privacitat de les persones usuàries i a garantir un tractament adequat de les dades personals, d’acord amb el Reglament (UE) 2016/679 (RGPD) i la Llei Orgànica 3/2018 (LOPDGDD).",
		sections: [
			{
				title: "Responsable del tractament",
				paragraphs: [
					"Responsable: COSA NOVA 17488, S.L.",
					"NIF: B24744716",
					"Correu electrònic: info@cosanova.cat",
				],
			},
			{
				title: "Dades que es recullen",
				paragraphs: [
					"Les dades personals que es poden recollir a través d’aquest lloc web són:",
					"• Nom i cognoms",
					"• Adreça de correu electrònic",
					"• Dades de contacte",
					"• Dades de facturació i enviament (en cas de compra)",
					"• Qualsevol altra informació que l’usuari/a faciliti voluntàriament mitjançant formularis",
				],
			},
			{
				title: "Finalitat del tractament",
				paragraphs: [
					"Les dades es tracten amb les següents finalitats:",
					"• Gestionar consultes i comunicacions",
					"• Gestionar comandes i processos de compra (si escau)",
					"• Enviar informació editorial, cultural o promocional (newsletter), amb consentiment previ",
					"• Complir obligacions legals",
				],
			},
			{
				title: "Legitimació",
				paragraphs: [
					"La base legal per al tractament de les dades és:",
					"• El consentiment explícit de l’usuari/a",
					"• L’execució d’un contracte o precontracte",
					"• El compliment d’obligacions legals",
				],
			},
			{
				title: "Conservació de les dades",
				paragraphs: [
					"Les dades personals es conservaran durant el temps necessari per complir amb la finalitat per a la qual van ser recollides i, posteriorment, durant els terminis exigits legalment.",
				],
			},
			{
				title: "Destinataris",
				paragraphs: [
					"Les dades no es comunicaran a tercers, excepte per obligació legal o quan sigui necessari per a la prestació del servei (per exemple, plataformes de pagament, enviament o hosting).",
				],
			},
			{
				title: "Drets de les persones usuàries",
				paragraphs: [
					"L’usuari/a pot exercir els drets d’accés, rectificació, supressió, oposició, limitació i portabilitat de les seves dades enviant una sol·licitud a info@cosanova.cat, indicant el dret que vol exercir.",
					"Així mateix, té dret a presentar una reclamació davant l’Agència Espanyola de Protecció de Dades (www.aepd.es).",
				],
			},
			{
				title: "Seguretat de les dades",
				paragraphs: [
					"COSA NOVA 17488 aplica les mesures tècniques i organitzatives necessàries per garantir la seguretat i confidencialitat de les dades personals.",
				],
			},
		],
	},
	"/politica-cookies/": {
		title: "Política de cookies",
		kicker: "Cookies",
		lead: "Informació sobre l’ús de cookies a cosanova.cat.",
		sections: [
			{
				title: "Què són les cookies?",
				paragraphs: [
					"Són petits fitxers que el navegador desa per permetre el funcionament del web, recordar preferències i obtenir estadístiques d’ús.",
					"Pots configurar o rebutjar les cookies no necessàries des del navegador.",
				],
			},
		],
	},
	"/reemborsaments_devolucions/": {
		title: "Política de reemborsaments i devolucions",
		kicker: "Botiga",
		lead: "Informació sobre enviaments, devolucions, compra i pagament.",
		sections: [
			{
				title: "Política d’enviaments",
				paragraphs: [
					"Les comandes es preparen i s’envien des de Cadaqués per l’equip de Cosa Nova 17488.",
					"Temps de preparació: aproximadament 2 dies laborables des de la confirmació de la comanda.",
					"Terminis estimats de lliurament:",
					"• Espanya peninsular: entre 2 i 5 dies laborables.",
					"• Illes Balears i Canàries: entre 4 i 10 dies laborables.",
					"• Unió Europea: entre 4 i 10 dies laborables.",
					"• Amèriques: entre 4 i 15 dies laborables.",
					"• Àfrica i Oceania: consultar.",
					"Els enviaments es realitzen a través de serveis de missatgeria gestionats mitjançant la plataforma Packlink Pro.",
					"Un cop la comanda hagi estat enviada, el client rebrà un correu electrònic amb la confirmació d’enviament i, si escau, el número de seguiment.",
				],
			},
			{
				title: "Política de devolucions",
				paragraphs: [
					"Si el llibre arriba defectuós o malmès durant el transport, el client pot sol·licitar una substitució dins dels 7 dies posteriors a la recepció de la comanda.",
					"Per tramitar la devolució, cal escriure a l’adreça de correu electrònic info@cosanova.cat, indicant el número de comanda i una fotografia del producte defectuós.",
					"Un cop revisada la incidència, Cosa Nova 17488 oferirà una substitució del llibre.",
					"No s’accepten devolucions de llibres en bon estat.",
				],
			},
			{
				title: "Condicions generals de compra",
				paragraphs: [
					"La botiga en línia de Cosa Nova 17488 ofereix la venda directa de llibres i altres productes realitzats per l’editorial.",
					"En realitzar una compra, el client accepta aquestes condicions generals.",
					"Els preus indicats a la web inclouen els impostos aplicables, però no inclouen les despeses d’enviament, que es calcularan abans de finalitzar la compra, tret que hi hagi alguna altra especificació com ara una oferta.",
					"Cosa Nova 17488 es reserva el dret de modificar preus, disponibilitat de productes o continguts de la botiga en línia en qualsevol moment.",
				],
			},
			{
				title: "Formes de pagament",
				paragraphs: [
					"Les comandes es poden pagar mitjançant targeta de crèdit o dèbit a través de Stripe, plataforma de pagament segur.",
				],
			},
			{
				title: "Experiència post-compra",
				paragraphs: [
					"Un cop finalitzada la compra, el client rebrà un correu electrònic de confirmació amb els detalls de la comanda.",
				],
			},
			{
				title: "Dades fiscals",
				paragraphs: [
					"Cosa Nova 17488 S.L.",
					"NIF: B24744716",
					"Carrer Calders, 22 – Cadaqués",
					"info@cosanova.cat",
					"www.cosanova.cat",
				],
			},
		],
	},
};
